import { Row, Col, Card, Badge, Button, Carousel } from "react-bootstrap";
import useSWR from "swr";
import AuthenticatedWrapper from "../../components/wrapper";
import {
  AWS_S3_BASE_URL,
  fetcher,
  getIconType,
  getParameterByName,
  serverUrl,
  swrConfig,
} from "../../helpers/utility";
import NavigationBack from "../../components/NavigationBack";
import style from "../../styles/locationDetails.module.scss";
import { format, isToday } from "date-fns";
import variables from "../../styles/utils.module.scss";
import Map from "../../components/map";
import { allNeedsMap } from "../../helpers/supportNeeds";
import { useCallback, useEffect, useState } from "react";
import SuitabilitySummary from "../../components/SuitabilitySummary";
import EnquiryModal from "./Search/enquiryModal";
import Loading from "../../components/loading";
import BasicModal from "../../components/BasicModal";

function LocationDetails({ user, isGoogleMapLoaded }) {
  const locationId = getParameterByName("locationId");
  const placementId = getParameterByName("placementId");
  const [supportItems, setSupportItems] = useState([]);
  const [suitabilityMatch, setSuitabilityMatch] = useState(null);
  const [viewEnquiryModal, setViewEnquiryModal] = useState(false);
  const [enquiryModalLocation, setEnquiryModalLocation] = useState(null);
  const [media, setMedia] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  const [showBasicModal, setShowModal] = useState(false);
  let locationData = {},
    locationError,
    locationIsLoading;

  if (locationId) {
    const { data, error, isLoading } = useSWR(
      `${serverUrl}/location/id/${locationId}`,
      fetcher,
      swrConfig
    );
    locationData = data;
    locationError = error;
    locationIsLoading = isLoading;
  }

  // Need to cross reference the location info with the placement info, if applicable
  const {
    data: placementData,
    error: placementError,
    isLoading: placementIsLoading,
  } = useSWR(
    placementId ? `${serverUrl}/placement/id/${placementId}?includeFilteredLocations=true` : null,
    fetcher,
    swrConfig
  );

  useEffect(() => {
    if (locationData?.location) {
      // Special parse filters
      if (
        locationData.location?.filters &&
        Object.keys(locationData.location.filters)?.length > 0
      ) {
        const newSupportItems = [];

        Object.keys(locationData.location.filters).forEach((filterKey) => {
          if (locationData.location.filters[filterKey] === true) {
            newSupportItems.push({ key: filterKey, label: allNeedsMap[filterKey] });
          }
        });

        setSupportItems(newSupportItems);
      }

      if (locationData?.location?.galleryURLs) {
        const images = locationData?.location?.galleryURLs.filter((media) => {
          return media?.type?.includes("image");
        });

        const videos = locationData?.location?.galleryURLs.filter((media) => {
          return media?.type?.includes("video");
        });

        setMedia({ images, videos });
      }
    }
  }, [locationData]);

  useEffect(() => {
    if (placementData?.filteredLocations) {
      const placementLocation = placementData?.filteredLocations.find((location) => {
        return location._id === locationId;
      });

      if (placementLocation?.match) {
        setSuitabilityMatch(placementLocation.match);
      }
    }
  }, [placementData?.filteredLocations]);

  const openEnquiryModal = useCallback((location) => {
    setViewEnquiryModal(true);
    setEnquiryModalLocation(location);
  }, []);

  function closeModal() {
    setViewEnquiryModal(false);
    setEnquiryModalLocation(null);
  }

  function hideBasicModal() {
    setShowModal(false);
    setModalConfig(null);
  }

  const primaryTileStyle = {
    display: "flex",
    flexDirection: "row",
    flexGrow: 1,
    flexBasis: "50%",
    flexShrink: 0,
    alignSelf: "center",
    height: 300,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const imageTileStyle = {
    width: "calc(50% - 4px)",
    height: 149,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    marginLeft: 2,
  };

  function getAgeRangeDescription(range) {
    let response = ` between the ages of `;

    if (!range || range?.length === 0) {
      response = `, no age ranges have been provided`;
      return response;
    }

    if (range?.length === 1) {
      const rangeItem = range[0];

      if (rangeItem.maximum > 36) {
        response += `${rangeItem.minimum > 0 ? rangeItem.minimum / 12 : 0} to ${
          rangeItem.maximum > 0 ? rangeItem.maximum / 12 : 0
        } years old`;
      } else {
        response += `${rangeItem.minimum} to ${rangeItem.maximum} months old`;
      }

      return response;
    }

    if (range?.length > 1) {
      range.forEach((rangeItem, index) => {
        if (index === range.length - 1) {
          response += ` and `;
        }

        if (rangeItem.maximum > 36) {
          response += `${rangeItem.minimum > 0 ? rangeItem.minimum / 12 : 0}-${
            rangeItem.maximum > 0 ? rangeItem.maximum / 12 : 0
          } years`;
        } else {
          response += `${rangeItem.minimum}-${rangeItem.maximum} months`;
        }
      });

      response += ` old`;

      return response;
    }
  }

  function getAvailabilityLastUpdated() {
    if (!locationData?.location?.capacityUpdated) {
      return "recently.";
    }

    return isToday(locationData?.location?.capacityUpdated)
      ? "Today"
      : format(locationData?.location?.capacityUpdated, "dd/MM/yyyy");
  }

  function showImageModal({ urls }) {
    setModalConfig({
      message: (
        <Carousel data-bs-theme="light">
          {urls?.length > 0
            ? urls.map((url) => {
                return (
                  <Carousel.Item key={url}>
                    <div className="d-flex flex-row justify-content-center align-items-center">
                      <img
                        src={`${AWS_S3_BASE_URL}/${url?.externalId}`}
                        style={{ maxWidth: "100%", maxHeight: "80vh" }}
                      />
                    </div>
                  </Carousel.Item>
                );
              })
            : null}
        </Carousel>
      ),
      size: "xl",
    });
    setShowModal(true);
  }

  if (locationIsLoading || placementIsLoading) {
    return <Loading />;
  }

  return (
    <AuthenticatedWrapper user={user}>
      <Row style={{ padding: "5px 0px 60px" }}>
        <Col xs={12} lg={10} className="offset-lg-1">
          <NavigationBack />

          <div className="d-flex flex-row" style={{}}>
            {media?.images?.length > 0 && media.images[0]?.externalId ? (
              <div
                key={media.images[0].externalId}
                style={{
                  backgroundImage: `url(${`${AWS_S3_BASE_URL}/${media.images[0].externalId}`})`,
                  ...primaryTileStyle,
                }}
                onClick={() => {
                  showImageModal({
                    urls: media.images,
                  });
                }}
              ></div>
            ) : null}

            <div
              className="d-flex flex-row flex-wrap"
              style={{ flex: 1, flexShrink: 0, flexBasis: "50%", alignSelf: "center" }}
            >
              {media?.images?.length > 0
                ? media.images?.map((image, index) => {
                    if (index === 0) {
                      return null;
                    }

                    return (
                      <div
                        key={image.externalId}
                        style={{
                          backgroundImage: `url(${`${AWS_S3_BASE_URL}/${image.externalId}`})`,
                          ...imageTileStyle,
                          marginBottom: 2,
                          height: media.images?.length < 4 ? 300 : 149,
                        }}
                        onClick={() => {
                          showImageModal({
                            urls: [
                              media.images[index],
                              ...media.images.filter((currentIndex) => {
                                return currentIndex !== index;
                              }),
                            ],
                          });
                        }}
                      ></div>
                    );
                  })
                : null}
            </div>
          </div>

          <Row style={{ marginTop: 10 }}>
            <Col xs={12} lg={8}>
              <Card>
                <Card.Body className="card-body">
                  <div className="d-flex flex-row">
                    <div style={{ flexGrow: 1 }}>
                      <h1
                        className="h1"
                        style={{
                          marginBottom: 0,
                          display: "flex",
                          alignItems: "center",
                          color: variables.green,
                        }}
                      >
                        {locationData?.location.name}

                        {locationData?.location?.ofstedReport?.isClosed ? (
                          <Badge
                            bg="danger"
                            style={{ marginLeft: 10, fontSize: 18 }}
                            title={`The Ofsted URN for this location has been reported as closed. Source: ofsted.gov.uk.`}
                          >
                            Closed
                          </Badge>
                        ) : null}
                      </h1>
                    </div>

                    <div>
                      <Button
                        onClick={() => {
                          openEnquiryModal(locationData?.location);
                        }}
                        variant={"primary"}
                        title={`Enquire about ${locationData?.location.name}`}
                      >
                        ENQUIRE
                      </Button>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <p
                      className="fst-italic fw-lighter"
                      style={{
                        color: variables.darkGrey,
                      }}
                    >
                      {locationData?.location?.address?.addressLine1},{" "}
                      {locationData?.location?.address?.addressTownCity ||
                        locationData?.location?.address?.county ||
                        "UK"}
                    </p>
                  </div>

                  <h2 className={"h3"}>About </h2>

                  <p>{locationData?.location?.description}</p>

                  {locationData?.location?.address?.lat && locationData?.location?.address?.lng ? (
                    <div style={{ marginTop: 30, width: "100%", height: 300 }}>
                      <Map
                        isLoaded={isGoogleMapLoaded}
                        heightOverride={300}
                        center={{
                          lat: locationData?.location?.address?.lat,
                          lng: locationData?.location?.address?.lng,
                        }}
                        locations={[
                          {
                            key: "location",
                            position: {
                              lat: locationData?.location?.address?.lat,
                              lng: locationData?.location?.address?.lng,
                            },
                          },
                        ]}
                      />
                    </div>
                  ) : null}
                </Card.Body>
              </Card>

              {media?.videos?.length > 0 ? (
                <Card style={{ marginTop: 20 }}>
                  <Card.Body className="card-body">
                    <h3 className={"h3"}>Video</h3>

                    <video controls style={{ width: "100%" }}>
                      <source
                        src={`${AWS_S3_BASE_URL}/${media?.videos[0].externalId}`}
                        type={media?.videos[0].type}
                      />
                    </video>
                  </Card.Body>
                </Card>
              ) : null}

              <Card style={{ marginTop: 20 }}>
                <Card.Body className="card-body">
                  <h3 className={"h3"}>Availability</h3>

                  <div>
                    {locationData?.location.name} provides care for upto{" "}
                    {locationData?.location?.capacityMax} children
                    {getAgeRangeDescription(locationData?.location?.ageRangeInMonths)}.{" "}
                    {locationData?.location?.staffPersonInCharge} is the person in charge of care,
                    leading a team of {locationData?.location?.staffCount} staff.
                    {locationData?.location?.typeOfCare?.length > 0
                      ? ` They offer ${locationData?.location?.typeOfCare.join(", ")} care.`
                      : ""}
                  </div>

                  <div>
                    Availability information was last updated {getAvailabilityLastUpdated()}
                  </div>
                </Card.Body>
              </Card>

              {supportItems?.length > 0 ? (
                <Card style={{ marginTop: 20 }}>
                  <Card.Body className="card-body">
                    <h3 className={"h3"}>Support Available</h3>

                    <p>
                      {locationData?.location.name} has indicated that they are able to provide the
                      following support.
                      <br />
                      Please discuss any specific needs with them further.
                    </p>

                    {supportItems?.length > 0
                      ? supportItems?.map((item) => {
                          return (
                            <Badge
                              key={item.key}
                              bg="secondary"
                              style={{ marginRight: 6, fontSize: 12 }}
                            >
                              {item.label}
                            </Badge>
                          );
                        })
                      : null}
                  </Card.Body>
                </Card>
              ) : null}
            </Col>

            <Col xs={12} lg={4} className="mobile-gap">
              <Card>
                <Card.Body className="card-body">
                  <h2 className="h3" style={{ marginBottom: 15 }}>
                    Key Information
                  </h2>

                  {locationData?.location?.ofstedReport?.isClosed ? (
                    <div className={style.locationFact}>
                      <div className="fw-semibold error">
                        <i
                          className={`bi bi-exclamation-circle`}
                          style={{
                            fontSize: "12px",
                            color: variables.red,
                            marginRight: 6,
                          }}
                        ></i>
                        Location Closed
                      </div>
                      <div className="error">Ofsted have reported this location is closed.</div>
                    </div>
                  ) : null}

                  <div className={style.locationFact}>
                    <div className="fw-semibold">
                      <i
                        className={`bi bi-house`}
                        style={{
                          fontSize: "14px",
                          color: variables.black,
                          marginRight: 6,
                        }}
                      ></i>
                      Availability
                    </div>
                    <div
                      className="text-truncate"
                      title={
                        locationData?.location?.capacityUpdated
                          ? `Updated: ${
                              isToday(locationData?.location?.capacityUpdated)
                                ? "Today"
                                : format(locationData?.location?.capacityUpdated, "dd/MM/yyyy")
                            }`
                          : null
                      }
                    >
                      {locationData?.location?.capacityMax - locationData?.location?.capacityUsed >=
                      5
                        ? "Good"
                        : ""}
                      {locationData?.location?.capacityMax - locationData?.location?.capacityUsed <
                        5 &&
                      locationData?.location?.capacityMax - locationData?.location?.capacityUsed >=
                        1
                        ? "Limited"
                        : ""}
                      {locationData?.location?.capacityMax -
                        locationData?.location?.capacityUsed ===
                      0
                        ? "None"
                        : ""}

                      {locationData?.location?.capacityUpdated ? (
                        <i
                          className={`bi bi-info-circle`}
                          style={{
                            fontSize: "14px",
                            color: variables.black,
                            marginLeft: 4,
                          }}
                        ></i>
                      ) : null}
                    </div>
                  </div>

                  {locationData?.location?.ofstedReport?.inspections?.length > 0 ? (
                    <div className={style.locationFact}>
                      <div className="fw-semibold">
                        <i
                          className={`bi bi-calendar-check`}
                          style={{
                            fontSize: "14px",
                            color: variables.black,
                            marginRight: 6,
                          }}
                        ></i>
                        Last Inspection
                      </div>
                      <div className="text-truncate">
                        <a
                          href={locationData.location?.ofstedReport?.inspections[0]?.link}
                          target="_blank"
                        >
                          {locationData.location?.ofstedReport?.inspections[0]?.name}
                        </a>
                      </div>
                    </div>
                  ) : null}

                  {locationData?.location?.website ? (
                    <div className={style.locationFact}>
                      <div className="fw-semibold">
                        <i
                          className={`bi bi-box-arrow-up-right`}
                          style={{
                            fontSize: "14px",
                            color: variables.black,
                            marginRight: 6,
                          }}
                        ></i>
                        Website
                      </div>
                      <div className="text-truncate">
                        <a target="_blank" href={locationData?.location?.website}>
                          {locationData?.location?.website}
                        </a>
                      </div>
                    </div>
                  ) : null}

                  {locationData?.location?.email ? (
                    <div className={style.locationFact}>
                      <div className="fw-semibold">
                        <i
                          className={`bi bi-envelope`}
                          style={{
                            fontSize: "14px",
                            color: variables.black,
                            marginRight: 6,
                          }}
                        ></i>
                        Email
                      </div>
                      <div className="text-truncate">
                        <a href={`mailto:${locationData?.location?.email}`}>
                          {locationData?.location?.email}
                        </a>
                      </div>
                    </div>
                  ) : null}

                  {locationData?.location?.phone ? (
                    <div className={style.locationFact}>
                      <div className="fw-semibold">
                        <i
                          className={`bi bi-telephone`}
                          style={{
                            fontSize: "14px",
                            color: variables.black,
                            marginRight: 6,
                          }}
                        ></i>
                        Phone
                      </div>
                      <div className="text-truncate">
                        <a href={`tel:${locationData?.location?.phone}`}>
                          {locationData?.location?.phone}
                        </a>
                      </div>
                    </div>
                  ) : null}
                </Card.Body>
              </Card>

              {suitabilityMatch ? (
                <Card style={{ marginTop: 20 }}>
                  <Card.Body className="card-body">
                    <h2 className="h3" style={{ marginBottom: 15 }}>
                      Suitability
                    </h2>

                    <SuitabilitySummary match={suitabilityMatch} hideHeader={true} />
                  </Card.Body>
                </Card>
              ) : null}

              {locationData?.location?.documents?.length > 0 ? (
                <Card style={{ marginTop: 20 }}>
                  <Card.Body className="card-body">
                    <h2 className="h3" style={{ marginBottom: 15 }}>
                      Documents
                    </h2>

                    <div>
                      {locationData.location.documents.map((document) => {
                        return (
                          <div className={style.locationFact}>
                            <a href={document.expiringUrl} target="_blank">
                              <div className="fw-normal">
                                <i
                                  className={`bi ${getIconType(document.type)}`}
                                  style={{
                                    fontSize: "14px",
                                    color: variables.black,
                                    marginRight: 6,
                                  }}
                                ></i>
                                {document.name}
                              </div>
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </Card.Body>
                </Card>
              ) : null}
            </Col>
          </Row>
        </Col>
      </Row>

      <EnquiryModal
        placement={placementData?.placement}
        show={viewEnquiryModal}
        location={enquiryModalLocation}
        close={closeModal}
        size={"lg"}
        user={user}
      />

      <BasicModal
        title={modalConfig?.title}
        message={modalConfig?.message}
        show={showBasicModal}
        close={hideBasicModal}
        size={modalConfig?.size || "md"}
        buttons={modalConfig?.buttons}
        borderLess={true}
      />
    </AuthenticatedWrapper>
  );
}

export default LocationDetails;
